/**
 * Module: FindsScreen
 * Role: Overlay-page at /finds — 2-col grid of all buyer-taken slots with status chips.
 *       Backend expands `status=all_taken` server-side and returns API slot statuses
 *       (assigned/in_progress/pending_review/approved/expired/cancelled).
 *
 * Visual: matches prototype.jsx FindsScreen (screens.jsx:824-844) — 2-col grid,
 * Card p=0 with overflow:hidden, product image h=150, marketplace badge overlay,
 * 2-line title clamp, Caveat "+X ₽" cashback annotation.
 *
 * Backend contract:
 *   - GET /api/v2/buyer/slots?status=all_taken&limit=20&offset=N → SlotListResponse
 *
 * Uses:
 *   - atoms (IOSScreen, PageHeader, Card, ProductImg, Skeleton, EmptyState, GlassPill)
 *   - @tanstack/react-query (useInfiniteQuery)
 *   - api/slots (fetchTakenSlotsPage, getNextSlotsPageParam)
 *   - hooks/usePlatformBack (TG BackButton)
 *
 * Used by:
 *   - App.tsx (route /finds, sibling of AppShell)
 */
import { CSSProperties, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  IOSScreen, PageHeader, Card, ProductImg, Skeleton, EmptyState, GlassPill,
} from '../components/atoms';
import {
  fetchTakenSlotsPage,
  getNextSlotsPageParam,
  type SlotListItem,
  type SlotListResponse,
} from '../api/slots';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { P, S, T, marketplaceBadgeStyle } from '../styles/tokens';

// ─── Status chip (FTR-1233) ──────────────────────────────────────────────────
const STATUS_CHIP_MAP: Record<string, { label: string; bg: string; color: string }> = {
  assigned:         { label: 'В работе',     bg: `${P.terracotta}18`, color: P.terracotta },
  in_progress:      { label: 'В работе',     bg: `${P.terracotta}18`, color: P.terracotta },
  pending_review:   { label: 'На проверке',  bg: `${P.warn}18`,       color: P.warn },
  approved:         { label: 'Выполнено',    bg: `${P.jade}18`,       color: P.jade },
  expired:          { label: 'Сгорело',      bg: `${P.muted}18`,      color: P.muted },
  cancelled:        { label: 'Отменено',     bg: `${P.muted}18`,      color: P.muted },
};

function StatusChip({ status }: { status: string }) {
  const chip = STATUS_CHIP_MAP[status] ?? { label: status, bg: `${P.muted}18`, color: P.muted };
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      lineHeight: 1.5,
      background: chip.bg,
      color: chip.color,
    }}>
      {chip.label}
    </span>
  );
}

// ─── FindsCard ────────────────────────────────────────────────────────────────

interface FindsCardProps {
  slot: SlotListItem;
  onTap: () => void;
}

function FindsCard({ slot, onTap }: FindsCardProps) {
  const title = slot.product_title?.trim() || 'Задание';
  const marketplace = slot.marketplace?.trim() || '';
  const hue = title.charCodeAt(0) % 360;
  const rewardRubles = Math.round(slot.reward_kopecks / 100);
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = imageFailed ? null : slot.product_image_url;

  return (
    <Card
      p={0}
      r={16}
      hover
      onClick={onTap}
      style={{ overflow: 'hidden', cursor: 'pointer' }}
    >
      {/* ── Image ── */}
      <div style={{ position: 'relative' }}>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={title}
            onError={() => setImageFailed(true)}
            style={{
              width: '100%',
              height: 150,
              objectFit: 'cover',
              display: 'block',
            }}
          />
        ) : (
          <ProductImg h={150} w="100%" r={0} hue={hue} />
        )}

        {/* Marketplace badge — top-left brand pill (Ozon blue / WB violet) */}
        {marketplace && (
          <span
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              ...marketplaceBadgeStyle(marketplace),
              padding: '3px 9px',
              borderRadius: 6,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.2,
              lineHeight: 1.4,
            }}
          >
            {marketplace}
          </span>
        )}
      </div>

      {/* ── Card body ── */}
      <div style={{ padding: '8px 10px 10px' }}>
        {/* Title — 2-line clamp, subheadline 600 per prototype vitrina grid */}
        <div
          style={{
            font: T.subhead,
            fontWeight: 600,
            color: S.label,
            lineHeight: 1.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: 36,
          } as CSSProperties}
        >
          {title}
        </div>

        {/* Status chip + step indicator (FTR-1233) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <StatusChip status={slot.status} />
          {typeof slot.current_step === 'number' && slot.current_step > 0 && (
            <span style={{ font: T.captionAlt, color: S.secondaryLabel }}>
              Шаг {slot.current_step}
            </span>
          )}
        </div>

        {/* Marketplace label + Caveat reward row (prototype screens.jsx:835-838) */}
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 6,
            marginTop: 6,
          }}
        >
          <span
            style={{
              font: T.captionAlt,
              color: S.secondaryLabel,
            }}
          >
            {marketplace}
          </span>
          <span
            style={{
              fontFamily: T.fontAccent,
              fontSize: 20,
              color: P.terraDark,
              fontWeight: 700,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            +{rewardRubles}&nbsp;₽
          </span>
        </div>
      </div>
    </Card>
  );
}

// ─── FindsScreen ─────────────────────────────────────────────────────────────
export default function FindsScreen() {
  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  usePlatformBack();

  // Step 1: all taken slots — backend expands 'all_taken' to all 9 buyer statuses (FTR-1233)
  const slotsQuery = useInfiniteQuery<SlotListResponse>({
    queryKey: ['buyer', 'slots', 'all_taken'],
    queryFn: ({ pageParam }) => fetchTakenSlotsPage(pageParam as number),
    initialPageParam: 0,
    getNextPageParam: getNextSlotsPageParam,
    staleTime: 30_000,
    retry: false,
  });

  const slots: SlotListItem[] = useMemo(
    () => slotsQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [slotsQuery.data],
  );

  return (
    <IOSScreen tabBarSpace={false}>
      <PageHeader title="Мои находки" subtitle="Ваши задания" onBack={onBack} />

      {slotsQuery.isPending && <FindsSkeleton />}

      {slotsQuery.isError && (
        <div style={{ padding: '0 16px' }}>
          <EmptyState title="Не удалось загрузить" subtitle="Попробуй позже" />
        </div>
      )}

      {slots.length === 0 && !slotsQuery.isPending && !slotsQuery.isError && (
        <div style={{ padding: '0 16px' }}>
          <EmptyState
            title="Пока пусто"
            subtitle="Возьмите задание во вкладке «Задания»"
          />
        </div>
      )}

      {slots.length > 0 && !slotsQuery.isPending && !slotsQuery.isError && (
        <div
          style={{
            padding: '14px 12px 0',
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
          }}
        >
          {slots.map((s) => (
            <FindsCard
              key={s.id}
              slot={s}
              onTap={() => navigate(`/flow/${s.id}`)}
            />
          ))}
          {slotsQuery.hasNextPage && !slotsQuery.isFetchingNextPage && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
              <GlassPill onClick={() => { slotsQuery.fetchNextPage(); }}>Показать ещё</GlassPill>
            </div>
          )}
          {slotsQuery.isFetchingNextPage && (
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
              <GlassPill>Загрузка...</GlassPill>
            </div>
          )}
        </div>
      )}
    </IOSScreen>
  );
}

// ─── Skeleton (6 cards, 2 cols × 3 rows) ────────────────────────────────────
function FindsSkeleton() {
  return (
    <div
      style={{
        padding: '14px 12px 0',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 10,
      }}
    >
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div key={i}>
          <Skeleton h={150} r={16} />
          <Skeleton h={14} w="80%" style={{ marginTop: 8 }} />
          <Skeleton h={12} w="55%" style={{ marginTop: 6 }} />
        </div>
      ))}
    </div>
  );
}
